// Trilhas do fundo de /entrar: mesma curva, paleta e shader da referência NeuroSync, em WebGL2 puro.
// Sem Three.js: cada trilha é uma fita instanciada, e o desfoque do primeiro plano fica no CSS.

const LINES = 100;
const SEGMENTS = 200;
const SPEED = 0.1;
const FLOOR_LENGTH = 132.75;
const BEND_START_Z = -150;
const ARC_RADIUS = 10;
const WALL_HEIGHT = 200;
const MAX_DPR = 1.5;
// Espelha tokens.json > login.trails.
const PALETTE = ['#0b101d', '#334155', '#cc8066', '#ffffff', '#1f2937'];
// [reflexo, largura, brilho]: fita, halo e reflexo no piso.
const PASSES = [
  [0, 1, 0],
  [0, 7, 1],
  [1, 1, 0],
];

const VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aPath;
layout(location = 1) in vec2 aRibbon;
layout(location = 2) in vec2 aLine;
layout(location = 3) in vec3 aColor;
layout(location = 4) in vec3 aMotion;
uniform float uAspect;
uniform float uReflection;
uniform float uWidth;
out vec2 vUv;
out vec3 vColor;
out vec3 vMotion;
const float NEAR = 1.0;
const float FAR = 2000.0;
void main() {
  vUv = vec2(aRibbon.x, aRibbon.y * 0.5 + 0.5);
  vColor = aColor;
  vMotion = aMotion;
  float y = uReflection > 0.5 ? -aPath.x - 1.0 : aPath.x;
  vec3 view = vec3(aLine.x + aRibbon.y * aLine.y * uWidth, y, aPath.y) - vec3(0.0, 20.0, 140.0);
  float focal = 1.0 / tan(radians(27.5));
  gl_Position = vec4(
    view.x * focal / uAspect,
    view.y * focal,
    view.z * (FAR + NEAR) / (NEAR - FAR) + 2.0 * FAR * NEAR / (NEAR - FAR),
    -view.z
  );
}`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
in vec3 vColor;
in vec3 vMotion;
uniform float uTime;
uniform float uBendUv;
uniform float uReflection;
uniform float uGlow;
out vec4 fragColor;
const float BRIGHTNESS = 4.0131;
const float GAIN = 0.9;
const float GLOW_STRENGTH = 0.22;
const float DOT_DENSITY = 70.0;
const float DOT_SIZE = 0.25;
const float DOT_SPEED = 1.5;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

vec3 toSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  float speed = vMotion.x;
  float offset = vMotion.y;
  float tail = vMotion.z;
  float intensity = uReflection > 0.5 ? 0.4 : 1.0;

  float dist = fract(fract(uTime * speed + offset) - vUv.x + 1.0);
  float base = pow(max(0.0, smoothstep(tail, 0.0, dist)), 1.2);
  // Sem isso a cabeça da trilha termina num corte reto; a referência a arredonda com o bloom.
  base *= smoothstep(0.0, 0.015, dist);
  float core = pow(max(0.0, base), 3.0) * 1.5;

  float signalPos = (vUv.x - uTime * speed * DOT_SPEED - offset) * DOT_DENSITY;
  float dotId = floor(signalPos);
  float centerDist = length(vec2((fract(signalPos) - 0.5) * 2.0, (vUv.y - 0.5) * 2.0));
  float dotShape = 1.0 - smoothstep(0.0, DOT_SIZE, centerDist);
  float dotValue = dotShape * step(0.6, hash(vec2(dotId, offset)))
    * (sin(uTime * 4.0 + hash(vec2(dotId)) * 6.28) * 0.3 + 0.7) * base;

  if (uReflection > 0.5) {
    float fade = 1.0 - smoothstep(uBendUv - 0.015, uBendUv, vUv.x);
    base *= fade;
    core *= fade;
    dotValue *= fade * 0.1;
    base = pow(max(0.0, base), 0.5) * (0.7 + hash(vUv * 300.0 + uTime * 0.05) * 0.3);
    core *= 0.3;
  }

  vec3 trail = vColor * (base + core * 1.5) * intensity * BRIGHTNESS;
  vec3 rgb = trail / max(1.0 - clamp(dotValue * 1.8, 0.0, 0.95), 0.001)
    + vColor * dotValue * 2.5 * intensity * BRIGHTNESS;
  float alpha = (base + dotValue) * intensity;
  // A passada larga imita o bloom da referência: mesma cor, caindo até a borda da fita.
  float across = 1.0 - abs(vUv.y - 0.5) * 2.0;
  float glow = mix(1.0, across * across * GLOW_STRENGTH, uGlow);
  fragColor = vec4(toSrgb(clamp(rgb * alpha * GAIN * glow, 0.0, 1.0)), 1.0);
}`;

function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearColor(hex: string): number[] {
  return [1, 3, 5].map((start) => srgbToLinear(parseInt(hex.slice(start, start + 2), 16) / 255));
}

// Piso reto, curva suave e parede vertical; devolve [y, z] para d em unidades de comprimento.
function pathPoint(d: number, flat: number, arc: number): [number, number] {
  if (d <= flat) return [0, FLOOR_LENGTH - d];
  if (d <= flat + arc) {
    const norm = (d - flat) / arc;
    const eased = norm * norm * (3 - 2 * norm);
    const angle = (norm * 0.4 + eased * 0.6) * (Math.PI * 0.5);
    return [ARC_RADIUS * (1 - Math.cos(angle)), BEND_START_Z - Math.sin(angle) * ARC_RADIUS];
  }
  return [ARC_RADIUS + (d - flat - arc), BEND_START_Z - ARC_RADIUS];
}

function ribbonVertices(flat: number, arc: number, total: number): Float32Array {
  const vertices: number[] = [];
  for (let i = 0; i <= SEGMENTS; i += 1) {
    const u = i / SEGMENTS;
    const [y, z] = pathPoint(u * total, flat, arc);
    vertices.push(y, z, u, -1, y, z, u, 1);
  }
  return new Float32Array(vertices);
}

function lineInstances(): Float32Array {
  const instances: number[] = [];
  for (let i = 0; i < LINES; i += 1) {
    const norm = (i / (LINES - 1)) * 2 - 1;
    const spread = norm * 0.5 + Math.sign(norm) * Math.abs(norm) ** 1.2 * 0.5;
    const color = linearColor(PALETTE[Math.floor(Math.random() * PALETTE.length)] ?? '#ffffff');
    instances.push(
      spread * 80 + (Math.random() - 0.5) * 2,
      Math.random() * 0.2 + 0.1,
      ...color,
      Math.random() * 0.5 + 0.2,
      Math.random(),
      Math.random() * 0.4 + 0.3,
    );
  }
  return new Float32Array(instances);
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return shader;
}

function linkProgram(gl: WebGL2RenderingContext): WebGLProgram | null {
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
  // O fundo é decorativo: sem ele, o login segue normal sobre o fundo escuro.
  console.warn('jnr.login.trilhas_indisponiveis', gl.getProgramInfoLog(program), gl.getShaderInfoLog(fragment));
  return null;
}

function bindBuffers(gl: WebGL2RenderingContext, ribbon: Float32Array, lines: Float32Array): void {
  gl.bindVertexArray(gl.createVertexArray());

  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, ribbon, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);

  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, lines, gl.STATIC_DRAW);
  const stride = 8 * Float32Array.BYTES_PER_ELEMENT;
  [
    [2, 2, 0],
    [3, 3, 8],
    [4, 3, 20],
  ].forEach(([location = 0, size = 0, offset = 0]) => {
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset);
    gl.vertexAttribDivisor(location, 1);
  });
}

// failIfMajorPerformanceCaveat não recusa o SwiftShader do Chromium.
function rendersInSoftware(gl: WebGL2RenderingContext): boolean {
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  return info !== null && /swiftshader|llvmpipe|software|basic render/i.test(String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)));
}

export function bindLoginTrails(): void {
  const canvas = document.querySelector<HTMLCanvasElement>('[data-login-trails]');
  if (!canvas || canvas.dataset.bound === 'true') return;
  canvas.dataset.bound = 'true';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Sem GPU cada quadro leva centenas de ms e trava o formulário: nesse caso a página fica sem trilhas.
  const gl = canvas.getContext('webgl2', { alpha: false, antialias: true, failIfMajorPerformanceCaveat: true });
  if (!gl || rendersInSoftware(gl)) return;
  const program = linkProgram(gl);
  if (!program) return;
  canvas.dataset.active = 'true';

  const flat = FLOOR_LENGTH - BEND_START_Z;
  const arc = Math.PI * ARC_RADIUS * 0.5;
  const total = flat + arc + (WALL_HEIGHT - ARC_RADIUS);
  bindBuffers(gl, ribbonVertices(flat, arc, total), lineInstances());

  gl.useProgram(program);
  gl.uniform1f(gl.getUniformLocation(program, 'uBendUv'), flat / total);
  const uTime = gl.getUniformLocation(program, 'uTime');
  const uAspect = gl.getUniformLocation(program, 'uAspect');
  const uReflection = gl.getUniformLocation(program, 'uReflection');
  const uWidth = gl.getUniformLocation(program, 'uWidth');
  const uGlow = gl.getUniformLocation(program, 'uGlow');
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE);

  let time = 0;
  let last = performance.now();
  const frame = (now: number): void => {
    if (!canvas.isConnected) return;
    // Com a aba oculta o navegador pausa o quadro; o limite evita o salto na volta.
    time += Math.min((now - last) / 1000, 0.1) * SPEED;
    last = now;

    const dpr = Math.min(window.devicePixelRatio, MAX_DPR);
    const width = Math.round(canvas.clientWidth * dpr);
    const height = Math.round(canvas.clientHeight * dpr);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    }

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(uTime, time);
    gl.uniform1f(uAspect, width / Math.max(height, 1));
    for (const [reflection = 0, width = 1, glow = 0] of PASSES) {
      gl.uniform1f(uReflection, reflection);
      gl.uniform1f(uWidth, width);
      gl.uniform1f(uGlow, glow);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, (SEGMENTS + 1) * 2, LINES);
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}
